import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { userService } from '../services/api';
import { Coordinates } from '../types/event';
import './LocationSharing.css';

interface LocationSharingProps {
  onLocationUpdate?: (coords: Coordinates) => void;
  showMap?: boolean;
  forcePrompt?: boolean;
}

// Exporter l'interface des méthodes exposées par la référence
export interface LocationSharingRef {
  updateLocation: () => void;
}

const LocationSharing = forwardRef<LocationSharingRef, LocationSharingProps>(({
  onLocationUpdate,
  showMap = false,
  forcePrompt = false
}, ref) => {
  const [location, setLocation] = useState<Coordinates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<PermissionState | null>(null);
  const [loading, setLoading] = useState(false);

  // Exposer la méthode updateLocation via la référence
  useImperativeHandle(ref, () => ({
    updateLocation: getLocation
  }));

  // Vérifier si l'utilisateur a déjà partagé sa position
  useEffect(() => {
    // Check permission status for UI updates
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName })
        .then(result => {
          setPermissionStatus(result.state);
          
          // If permission state changes, update our state
          result.onchange = () => {
            setPermissionStatus(result.state);
          };
          
          // Si les permissions sont déjà accordées, obtenir automatiquement la position
          if (result.state === 'granted') {
            console.log('📍 Localisation auto: permissions déjà accordées');
            getLocation();
          }
        })
        .catch(err => {
          console.error('Erreur lors de la vérification des permissions de géolocalisation:', err);
        });
    }
  }, []);

  // Fonction pour obtenir la position de l'utilisateur
  const getLocation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!navigator.geolocation) {
        throw new Error('La géolocalisation n\'est pas supportée par ce navigateur');
      }
      
      // Try to get location from browser
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Éviter de logger l'objet position complet pour garder la console propre
          const coords = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          console.log('📍 Position obtenue:', coords);
          
          // Convert coordinates to numbers if they're strings
          const lat = typeof position.coords.latitude === 'string' 
            ? parseFloat(position.coords.latitude) 
            : position.coords.latitude;
            
          const lon = typeof position.coords.longitude === 'string'
            ? parseFloat(position.coords.longitude)
            : position.coords.longitude;
          
          // Check if browser returned valid coordinates
          if (position.coords && 
              !isNaN(lat) && lat !== undefined &&
              !isNaN(lon) && lon !== undefined) {
            
            // Use browser coordinates if they're valid
            handleValidCoordinates({
              lat: lat,
              lon: lon
            });
          } else {
            console.error('Coordonnées invalides reçues du navigateur');
            // Use fallback coordinates for Montreal (Place des Arts)
            const fallbackCoords = {
              lat: 45.508888,
              lon: -73.561668
            };
            setError('Impossible d\'obtenir votre position précise. Utilisation des coordonnées par défaut pour Montréal.');
            handleValidCoordinates(fallbackCoords);
          }
        },
        (error) => {
          console.error('Erreur de géolocalisation du navigateur:', error);
          
          // Use fallback coordinates for Montreal (Place des Arts)
          const fallbackCoords = {
            lat: 45.508888,
            lon: -73.561668
          };
          
          let errorMessage = 'Impossible d\'obtenir votre position. Utilisation des coordonnées par défaut pour Montréal.';
          
          // More specific error messages
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Vous avez refusé l\'accès à votre position. Utilisation des coordonnées par défaut pour Montréal.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Votre position est indisponible. Utilisation des coordonnées par défaut pour Montréal.';
              break;
            case error.TIMEOUT:
              errorMessage = 'La demande de géolocalisation a expiré. Utilisation des coordonnées par défaut pour Montréal.';
              break;
          }
          
          setError(errorMessage);
          handleValidCoordinates(fallbackCoords);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000, 
          maximumAge: 0
        }
      );
    } catch (err) {
      console.error('Erreur lors de la demande de géolocalisation:', err);
      
      // Fallback to Montreal coordinates
      const fallbackCoords = {
        lat: 45.508888,
        lon: -73.561668
      };
      
      setError('Une erreur est survenue. Utilisation des coordonnées par défaut pour Montréal.');
      handleValidCoordinates(fallbackCoords);
    }
  };
  
  // Helper function to handle valid coordinates
  const handleValidCoordinates = (coords: Coordinates) => {
    // Éviter le log redondant car on a déjà loggé à l'étape précédente
    setLocation(coords);
    
    // Notify parent component
    if (onLocationUpdate) {
      onLocationUpdate(coords);
    }
    
    // Save to user profile
    userService.updateUserLocation(coords)
      .then(() => {
        console.log('✅ Position enregistrée dans le profil utilisateur');
      })
      .catch(err => {
        console.error('Erreur lors de la mise à jour de la localisation:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Simplified UI - just show the button when needed
  if (permissionStatus === 'granted') {
    return (
      <>
        {error && <div className="location-error">{error}</div>}
        <button 
          className="action-button refresh location-only-button" 
          onClick={getLocation}
          disabled={loading}
          title="Actualiser ma position"
        >
          {loading ? '...' : '📍 Actualiser'}
        </button>
        
        {showMap && location && (
          <div className="location-map">
            <iframe
              title="User Location"
              width="100%"
              height="200"
              frameBorder="0"
              src={`https://maps.google.com/maps?q=${location.lat},${location.lon}&z=15&output=embed`}
              allowFullScreen
            />
          </div>
        )}
      </>
    );
  }
  
  if (permissionStatus === 'denied') {
    return (
      <div className="location-error">
        Vous avez refusé l'accès à votre position. Pour voir les événements à proximité,
        veuillez modifier les paramètres de votre navigateur et autoriser la géolocalisation.
      </div>
    );
  }
  
  // Si forcePrompt est true ou si l'état des permissions est 'prompt', on affiche le bouton principal
  const shouldShowPrompt = forcePrompt || permissionStatus === 'prompt';
  
  // For 'prompt' state or when forcePrompt is true
  if (shouldShowPrompt) {
    return (
      <>
        {error && <div className="location-error">{error}</div>}
        <button 
          className="action-button primary location-only-button" 
          onClick={getLocation}
          disabled={loading}
        >
          {loading ? 'Localisation en cours...' : '📍 Utiliser ma position'}
        </button>
      </>
    );
  }
  
  // Default case - minimal button
  return (
    <>
      {error && <div className="location-error">{error}</div>}
      <button 
        className="action-button refresh location-only-button" 
        onClick={getLocation}
        disabled={loading}
      >
        {loading ? '...' : '📍'}
      </button>
    </>
  );
});

// Ajouter un nom pour le composant pour faciliter le débogage
LocationSharing.displayName = 'LocationSharing';

export default LocationSharing;
